import { CancelRounded, CheckCircleRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetUser } from "src/hooks";
import {
  invalidateProgramAndTasks,
  useGetTaskProgramDetail,
} from "src/hooks/queries/task-program-mutations";
import {
  invalidateTrainingQuiz,
  useGetTrainingQuiz,
} from "src/hooks/queries/training-quiz";
import type { TrainingQuizQuestion } from "src/services/backend";
import { EntitlementType } from "src/stores/models";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";
import { queryClient } from "src/utils/queryClient";
import {
  isHumanInTheLoopRejection,
  type HumanInTheLoopDecision,
} from "../../human-in-the-loop";
import { useHumanInTheLoop } from "../../HumanInTheLoopContext";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { ToolCallFrame } from "./ToolCallFrame";

type DraftTrainingQuizToolProps = {
  args: Record<string, unknown>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  actionIndex?: number;
};

const TRAINING_PERMISSION_ERROR =
  "You do not have permission to edit this training.";

const normalizeQuestions = (value: unknown): TrainingQuizQuestion[] => {
  if (!Array.isArray(value)) return [];

  return value.map((question, questionIndex) => {
    const raw =
      question && typeof question === "object"
        ? (question as Record<string, unknown>)
        : {};
    const options = Array.isArray(raw.options)
      ? raw.options.map((option, optionIndex) => {
          const rawOption =
            option && typeof option === "object"
              ? (option as Record<string, unknown>)
              : {};
          return {
            id:
              typeof rawOption.id === "string"
                ? rawOption.id
                : `q${questionIndex + 1}-o${optionIndex + 1}`,
            text: typeof rawOption.text === "string" ? rawOption.text : "",
          };
        })
      : [];

    return {
      id: typeof raw.id === "string" ? raw.id : `q${questionIndex + 1}`,
      text: typeof raw.text === "string" ? raw.text : "",
      options,
      correctOptionId:
        typeof raw.correctOptionId === "string" ? raw.correctOptionId : "",
    };
  });
};

const getQuestionValidationError = (
  questions: TrainingQuizQuestion[]
): string | null => {
  if (questions.length === 0) return "The quiz needs at least one question.";
  if (questions.some(({ text }) => !text.trim())) {
    return "Every question needs text.";
  }
  if (
    questions.some(
      ({ options }) =>
        options.length === 0 || options.some(({ text }) => !text.trim())
    )
  ) {
    return "Every question needs at least one non-empty answer.";
  }
  if (
    questions.some(
      ({ correctOptionId, options }) =>
        !options.some(({ id }) => id === correctOptionId)
    )
  ) {
    return "Choose one correct answer for every question.";
  }
  return null;
};

const getQuizContentSignature = (questions: TrainingQuizQuestion[]) =>
  JSON.stringify(
    questions.map(({ id, text, options, correctOptionId }) => ({
      id: id.trim(),
      text: text.trim(),
      options: options.map((option) => ({
        id: option.id.trim(),
        text: option.text.trim(),
      })),
      correctOptionId: correctOptionId.trim(),
    }))
  );

const getTraineeName = ({
  firstName,
  lastName,
  email,
}: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}) => [firstName, lastName].filter(Boolean).join(" ") || email;

const DraftTrainingQuizTool = ({
  args,
  toolResult,
  isLoading = false,
  actionIndex,
}: DraftTrainingQuizToolProps) => {
  const { orgId } = useChatDeviceContext();
  const humanInTheLoop = useHumanInTheLoop();
  const { data: user, isLoading: userLoading } = useGetUser();
  const canAdministerAllTrainings =
    !!user &&
    (user.permissions?.includes("write:configuration") ||
      hasFunctionalGroupEntitlement(
        user,
        orgId,
        EntitlementType.ADMINISTER_ALL_TRAININGS
      ));
  const initialQuestions = useMemo(
    () => normalizeQuestions(args.questions),
    [args.questions]
  );
  const [questions, setQuestions] = useState(initialQuestions);
  const [error, setError] = useState<string | null>(null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const invalidatedSuccessfulResult = useRef(false);
  const programId = typeof args.programId === "string" ? args.programId : "";
  const documentVersionId =
    typeof args.documentVersionId === "string" ? args.documentVersionId : "";
  const canLoadContext =
    canAdministerAllTrainings &&
    !isLoading &&
    !toolResult &&
    !!humanInTheLoop?.request &&
    !!programId &&
    !!documentVersionId;
  const { data: persistedQuiz, isLoading: quizLoading } = useGetTrainingQuiz({
    orgId,
    programId,
    documentVersionId,
    enabled: canLoadContext,
  });
  const { data: program, isLoading: programLoading } = useGetTaskProgramDetail(
    orgId,
    programId,
    { enabled: canLoadContext }
  );
  const isDraftVersion =
    program?.draftRevision?.documentVersionId === documentVersionId;
  const isPublished = !!persistedQuiz?.publishedAt;
  const isUnchangedPublishedQuiz =
    isPublished &&
    getQuizContentSignature(questions) ===
      getQuizContentSignature(normalizeQuestions(persistedQuiz?.questions));
  const completedTrainees = useMemo(
    () =>
      program?.occurrences.flatMap((occurrence) =>
        occurrence.quizId === persistedQuiz?.id
          ? occurrence.tasks.flatMap((task) =>
              task.assignee &&
              (task.manualStatus === "DONE" || !!task.trainingSignedAt)
                ? [
                    {
                      taskStateId: task.id,
                      name: getTraineeName(task.assignee),
                    },
                  ]
                : []
            )
          : []
      ) ?? [],
    [persistedQuiz?.id, program?.occurrences]
  );
  const pendingActionIndex =
    actionIndex ??
    (humanInTheLoop?.request?.action_requests.length === 1 ? 0 : undefined);
  const isPendingReview = pendingActionIndex !== undefined && !toolResult;
  const hasDecision =
    pendingActionIndex !== undefined &&
    !!humanInTheLoop?.decisions[pendingActionIndex];
  const actionLabel = isDraftVersion
    ? "Save draft"
    : isPublished
      ? "Overwrite Published Quiz"
      : "Publish";

  useEffect(() => {
    setQuestions(initialQuestions);
    setError(null);
  }, [initialQuestions]);

  useEffect(() => {
    if (toolResult?.status !== "success") {
      invalidatedSuccessfulResult.current = false;
      return;
    }
    if (
      invalidatedSuccessfulResult.current ||
      !programId ||
      !documentVersionId
    ) {
      return;
    }

    invalidatedSuccessfulResult.current = true;
    void invalidateTrainingQuiz(
      queryClient,
      orgId,
      programId,
      documentVersionId
    );
    invalidateProgramAndTasks(queryClient, orgId, programId);
  }, [documentVersionId, orgId, programId, toolResult?.status]);

  const submitDecision = (decision: HumanInTheLoopDecision) => {
    if (
      !humanInTheLoop?.request ||
      pendingActionIndex === undefined ||
      !isPendingReview
    ) {
      setError(
        "This quiz review is no longer active. Reload the conversation."
      );
      return;
    }

    humanInTheLoop.setDecision(pendingActionIndex, decision);
  };

  const submitQuestions = (confirmedCompletedTaskIds: string[]) => {
    const validationError = getQuestionValidationError(questions);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    submitDecision({
      type: "edit",
      edited_action: {
        name: "draft_training_quiz",
        args: {
          ...args,
          programId,
          documentVersionId,
          questions,
          confirmedCompletedTaskIds,
        },
      },
    });
  };

  const handlePrimaryAction = () => {
    if (isPublished && completedTrainees.length > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    submitQuestions([]);
  };

  const actionPending =
    userLoading ||
    isLoading ||
    quizLoading ||
    programLoading ||
    humanInTheLoop?.isLoading ||
    hasDecision ||
    !isPendingReview;
  const isRejected = isHumanInTheLoopRejection(toolResult);
  const hasToolError = toolResult?.status === "error" && !isRejected;
  const toolSucceeded = !!toolResult && toolResult.status !== "error";

  return (
    <>
      <ToolCallFrame
        title={`Training quiz proposal — ${program?.name || programId}`}
        isLoading={userLoading || isLoading}
        isRejected={isRejected}
        hasError={
          !isRejected &&
          ((!userLoading && !canAdministerAllTrainings) || hasToolError)
        }
        hasToolResult={!isRejected && !!toolResult}
        hasContent={!isRejected && questions.length > 0}
        expandedByDefault={!isRejected}
        footer={
          canAdministerAllTrainings && isPendingReview ? (
            <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<CancelRounded />}
                onClick={() => submitDecision({ type: "reject" })}
                disabled={actionPending}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleRounded />}
                onClick={handlePrimaryAction}
                disabled={actionPending || isUnchangedPublishedQuiz}
              >
                {actionLabel}
              </Button>
            </div>
          ) : null
        }
      >
        {!userLoading && !canAdministerAllTrainings ? (
          <Typography color="error" variant="body2" sx={{ p: 1 }}>
            {TRAINING_PERMISSION_ERROR}
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ p: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {toolSucceeded
                ? "The reviewed quiz change was applied."
                : "Review and edit this temporary proposal. Nothing is saved until you choose an action."}
            </Typography>

            {questions.map((question, questionIndex) => (
              <Stack key={question.id} spacing={1}>
                <TextField
                  label={`Question ${questionIndex + 1}`}
                  value={question.text}
                  multiline
                  onChange={(event) =>
                    setQuestions((current) =>
                      current.map((item, index) =>
                        index === questionIndex
                          ? { ...item, text: event.target.value }
                          : item
                      )
                    )
                  }
                  disabled={actionPending || !!toolResult}
                />
                {question.options.map((option, optionIndex) => (
                  <Stack
                    key={option.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Radio
                      checked={question.correctOptionId === option.id}
                      onChange={() =>
                        setQuestions((current) =>
                          current.map((item, index) =>
                            index === questionIndex
                              ? { ...item, correctOptionId: option.id }
                              : item
                          )
                        )
                      }
                      inputProps={{
                        "aria-label": `Question ${questionIndex + 1} correct answer ${optionIndex + 1}`,
                      }}
                      disabled={actionPending || !!toolResult}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={`Answer ${optionIndex + 1}`}
                      value={option.text}
                      onChange={(event) =>
                        setQuestions((current) =>
                          current.map((item, index) =>
                            index === questionIndex
                              ? {
                                  ...item,
                                  options: item.options.map((currentOption) =>
                                    currentOption.id === option.id
                                      ? {
                                          ...currentOption,
                                          text: event.target.value,
                                        }
                                      : currentOption
                                  ),
                                }
                              : item
                          )
                        )
                      }
                      disabled={actionPending || !!toolResult}
                    />
                  </Stack>
                ))}
              </Stack>
            ))}

            {hasToolError && (
              <Alert severity="error">The quiz change was not applied.</Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </ToolCallFrame>
      <Dialog
        open={confirmOverwriteOpen}
        onClose={() => setConfirmOverwriteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Overwrite published quiz?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              This will revert training completed by:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {completedTrainees.map(({ taskStateId, name }) => (
                <Typography component="li" variant="body2" key={taskStateId}>
                  {name}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2">Continue?</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverwriteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={actionPending}
            onClick={() => {
              setConfirmOverwriteOpen(false);
              submitQuestions(
                completedTrainees.map(({ taskStateId }) => taskStateId)
              );
            }}
          >
            Overwrite Published Quiz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DraftTrainingQuizTool;
